import { MessagingSidebar } from './MessagingSidebar';
import {
  Chat,
  ChatView,
  Channel,
  ChannelHeader,
  MessageInput,
  MessageList,
  Thread,
  Window,
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
  MessageActions,
} from 'stream-chat-react';
import type { MessageActionsProps } from 'stream-chat-react';
import { chatFormatDate } from '../helpers';
import { useChatNewMessage } from '../useChatNewMessage';
import { useEffect, useCallback } from 'react';
import { DefaultGenerics, StreamChat } from 'stream-chat';
import { ChannelListOptions } from '../viewModel/useInbox.viewModel';
import 'stream-chat-react/dist/css/v2/index.css';
import './chat.css';

const CustomUnreadMessagesSeparator = () => (
  <div className="str-chat__unread-messages-separator">New</div>
);

type InboxChatProps = {
  chatClient: StreamChat<DefaultGenerics>;
  userId: string;
  channelListOptions: ChannelListOptions;
  showUserInfo: (id: string) => void;
  blockChannel: (id: string) => void;
};

export const InboxChat = ({
  chatClient,
  userId,
  channelListOptions,
  showUserInfo,
  blockChannel,
}: InboxChatProps) => {
  return (
    <div className="p-2.5 bg-white rounded-[10px] w-full h-full max-h-[90%]">
      <Chat client={chatClient}>
        <ChatContent
          userId={userId}
          channelListOptions={channelListOptions}
          showUserInfo={showUserInfo}
          blockChannel={blockChannel}
        />
      </Chat>
    </div>
  );
};

const ChatContent = ({
  userId,
  channelListOptions,
  showUserInfo,
  blockChannel,
}: Pick<
  InboxChatProps,
  'userId' | 'channelListOptions' | 'blockChannel' | 'showUserInfo'
>) => {
  useChatNewMessage({ userId });

  return (
    <ChatView>
      <ChatView.Channels>
        <MessagingSidebar
          channelListOptions={channelListOptions}
          blockChannel={blockChannel}
          showUserInfo={showUserInfo}
        />
        <Channel
          MessageActions={CustomMessageActions}
          UnreadMessagesSeparator={CustomUnreadMessagesSeparator}
        >
          <ChannelContent />
          <Thread />
        </Channel>
      </ChatView.Channels>
    </ChatView>
  );
};

const ChannelContent = () => {
  const { jumpToFirstUnreadMessage } = useChannelActionContext();
  const { channel } = useChannelStateContext();

  useEffect(() => {
    if (channel) {
      jumpToFirstUnreadMessage();
    }
  }, [channel, channel?.cid, jumpToFirstUnreadMessage]);

  return (
    <Window>
      <ChannelHeader />
      <MessageList
        messageActions={['flag', 'mute', 'react', 'markUnread']}
        formatDate={chatFormatDate}
      />
      <MessageInput grow />
    </Window>
  );
};

/**
 * Custom MessageActions component that filters out 'markUnread' action
 * for the user's own messages.
 */
const CustomMessageActions = (props: MessageActionsProps) => {
  const { message } = useMessageContext();
  const { client } = useChatContext();

  const isOwnMessage = message?.user?.id === client.userID;

  // Override getMessageActions to filter out markUnread for own messages
  const getMessageActions = useCallback(() => {
    const baseActions: Array<'flag' | 'mute' | 'react' | 'markUnread'> = [
      'flag',
      'mute',
      'react',
    ];
    if (!isOwnMessage) {
      baseActions.push('markUnread');
    }
    return baseActions;
  }, [isOwnMessage]);

  return <MessageActions {...props} getMessageActions={getMessageActions} />;
};
